import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useWorkspaceSpeakerFormat } from '@src/hooks/useWorkspaceSpeakerFormat';
import CloseIcon from '@src/svgs/CloseIcon';
import SearchIcon from '@src/svgs/SearchIcon';
import { formatSpeakerRole } from '@src/utility/speakerRole';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import type { DatoSpeaker } from '@src/repositories/SpeakerRepository';

interface SpeakerWithColor extends DatoSpeaker {
  color: string;
}

interface AssignSpeakerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (datoSpeakerId: string) => Promise<boolean>;
  storySpeakers: SpeakerWithColor[];
  organizationSpeakers: SpeakerWithColor[];
  speakerLabel: string; // e.g., "spk_0"
  speakerDisplayName: string; // e.g., "Speaker 1"
  onCreateNewSpeaker: () => void;
  headerSubtitle?: string; // defaults to "Assign Speaker"
  headerTitle?: string; // defaults to "Select speaker for {speakerDisplayName}"
  /**
   * Raise this overlay above the dialog that opened it. Pass
   * `NESTED_IN_MODAL_Z` when opening from inside a shared `Modal`, whose
   * backdrop otherwise resolves higher than this one's and paints over it.
   * Omit from an ordinary panel — the resting z is correct there.
   */
  zIndex?: string;
}

type TabType = 'story' | 'organization';

/**
 * Modal for assigning an anonymous speaker to an existing Dato speaker.
 * Shows searchable lists of Story and Organization speakers.
 */
const AssignSpeakerModal: React.FC<AssignSpeakerModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  storySpeakers,
  organizationSpeakers,
  speakerDisplayName,
  onCreateNewSpeaker,
  headerSubtitle,
  headerTitle,
  zIndex,
}) => {
  // Each speaker's Title line reads as on their nametag (PLAT-12261).
  const speakerFormat = useWorkspaceSpeakerFormat();
  const [activeTab, setActiveTab] = useState<TabType>('story');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter speakers based on search query (only for organization tab)
  const filteredSpeakers = useMemo(() => {
    const speakers =
      activeTab === 'story' ? storySpeakers : organizationSpeakers;
    // Only apply search filter for organization tab
    if (activeTab !== 'organization' || !searchQuery.trim()) return speakers;

    const query = searchQuery.toLowerCase();
    return speakers.filter(
      s =>
        s.name?.toLowerCase().includes(query) ||
        s.title?.toLowerCase().includes(query) ||
        s.role?.toLowerCase().includes(query) ||
        s.organization?.toLowerCase().includes(query),
    );
  }, [activeTab, storySpeakers, organizationSpeakers, searchQuery]);

  // Clear search when switching tabs
  const handleTabChange = (tab: TabType) => {
    setSearchQuery('');
    setActiveTab(tab);
  };

  // Track if mousedown started on overlay (for proper close behavior)
  const mouseDownOnOverlay = useRef(false);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle overlay click - only close if both mousedown and mouseup were on overlay
  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    mouseDownOnOverlay.current = e.target === e.currentTarget;
  };

  const handleOverlayMouseUp = (e: React.MouseEvent) => {
    if (mouseDownOnOverlay.current && e.target === e.currentTarget) {
      onClose();
    }
    mouseDownOnOverlay.current = false;
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setActiveTab('story');
    }
  }, [isOpen]);

  const handleAssign = async (speaker: SpeakerWithColor) => {
    setIsAssigning(true);
    try {
      const success = await onAssign(speaker.id);
      if (success) {
        onClose();
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const portalContainer =
    typeof document !== 'undefined' ? document.body : null;

  if (!isOpen || !portalContainer) return null;

  return createPortal(
    <ModalOverlay
      $zIndex={zIndex}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
      data-selection-menu="true"
      data-testid="assign-speaker-modal"
    >
      <ModalContent ref={modalRef} onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <HeaderSubtitle>{headerSubtitle || 'Assign Speaker'}</HeaderSubtitle>
          <HeaderTitle>
            {headerTitle || `Select speaker for ${speakerDisplayName}`}
          </HeaderTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <TabsContainer>
          <Tab
            isActive={activeTab === 'story'}
            onClick={() => handleTabChange('story')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
            </svg>
            Story ({storySpeakers.length})
          </Tab>
          <Tab
            isActive={activeTab === 'organization'}
            onClick={() => handleTabChange('organization')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 21a8 8 0 0 0-16 0" />
              <circle cx="10" cy="8" r="5" />
              <path d="M22 20c0-3.37-2.69-6.29-6.44-7.38" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Organization ({organizationSpeakers.length})
          </Tab>
        </TabsContainer>

        {activeTab === 'organization' ? (
          <SearchContainer>
            <SearchIconWrapper>
              <SearchIcon width="16" height="16" />
            </SearchIconWrapper>
            <SearchInput
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search speakers..."
              autoComplete="off"
            />
          </SearchContainer>
        ) : null}

        <SpeakersList>
          {filteredSpeakers.length === 0 ? (
            <EmptyState>
              {activeTab === 'organization' && searchQuery
                ? 'No speakers match your search'
                : 'No speakers available'}
            </EmptyState>
          ) : (
            filteredSpeakers.map(speaker => (
              <SpeakerRow
                key={speaker.id}
                onClick={() => !isAssigning && handleAssign(speaker)}
                disabled={isAssigning}
                data-testid={`assign-speaker-confirm-${speaker.id}`}
              >
                <SpeakerAvatar
                  name={speaker.name || '?'}
                  color={speaker.color}
                  headshotUrl={speaker.headshots?.[0]?.url}
                  size={28}
                />
                <SpeakerInfo>
                  <SpeakerName>{speaker.name}</SpeakerName>
                  {formatSpeakerRole(speaker, speakerFormat) && (
                    <SpeakerTitle>
                      {formatSpeakerRole(speaker, speakerFormat)}
                    </SpeakerTitle>
                  )}
                </SpeakerInfo>
              </SpeakerRow>
            ))
          )}
        </SpeakersList>

        <FooterContainer>
          <CreateNewButton onClick={onCreateNewSpeaker} disabled={isAssigning}>
            + Create New Speaker
          </CreateNewButton>
        </FooterContainer>
      </ModalContent>
    </ModalOverlay>,
    portalContainer,
  );
};

// Styled Components
const ModalOverlay = styled.div<{ $zIndex?: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: var(--modal-blur);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${p => p.$zIndex ?? `var(${TOP_LAYER_VARS.modalBackdrop}, 1000006)`};
`;

const ModalContent = styled.div`
  background-color: var(--background);
  width: 400px;
  max-width: 90vw;
  max-height: 80vh;
  border: 1px solid var(--border);
  box-shadow: var(--elevation-toast);
  padding: var(--spacing-lg);
  position: relative;
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-md);
`;

const HeaderSubtitle = styled.h3`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  margin: 0;
`;

const HeaderTitle = styled.h2`
  color: var(--foreground);
  font-size: var(--text-lg);
  font-weight: var(--font-weight-bold);
  margin: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  right: var(--spacing-md);
  top: var(--spacing-md);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: var(--spacing-md);
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: var(--spacing-sm);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-xs) 36px;
  background: none;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  box-sizing: border-box;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const TabsContainer = styled.nav`
  display: flex;
  justify-content: flex-start;
  gap: 0 var(--spacing-lg);
  align-items: center;
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--spacing-sm) 0 0;
  margin-bottom: var(--spacing-md);
`;

const Tab = styled.button<{ isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) 0;
  margin: 0;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  transition: color var(--transition-fast);
  border: none;
  background: none;

  svg {
    flex-shrink: 0;
    opacity: 0.6;
  }

  &:hover {
    color: var(--foreground);
    svg {
      opacity: 1;
    }
  }

  ${props =>
    props.isActive &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium);
      svg {
        opacity: 1;
      }
      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;

const SpeakersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  overflow-y: auto;
  flex: 1;
  min-height: 200px;
  max-height: 300px;
  margin-bottom: var(--spacing-md);
`;

const SpeakerRow = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-sm);
  background: var(--sidebar-hover);
  border-radius: var(--radius);
  border: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: background var(--transition-normal) ease;
  width: 100%;
  text-align: left;

  &:hover:not(:disabled) {
    background: var(--sidebar-active);
  }
`;

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const SpeakerName = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpeakerTitle = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: var(--spacing-xl) var(--spacing-md);
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--spacing-sm);
`;

const CreateNewButton = styled.button`
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border-radius: var(--radius);
  border: none;
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default observer(AssignSpeakerModal);
